import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Check, X, Eye, ExternalLink, Loader2, AlertCircle,
  Clock, DollarSign, Image, Link2, Building, RefreshCw,
  Sparkles, Ban, CheckCircle
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * AdminAdApproval - Panel for admins to review and approve/reject ads
 */
export default function AdminAdApproval() {
  const { getAuthHeaders } = useAuth();
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('pending_review');
  const [selectedAd, setSelectedAd] = useState(null);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [processing, setProcessing] = useState(false);
  const [stats, setStats] = useState({ pending: 0, approved: 0, rejected: 0 });
  
  useEffect(() => {
    fetchAds();
    fetchStats();
  }, [activeTab]);
  
  const fetchAds = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/advertising/admin/ads?status=${activeTab}`, {
        headers: getAuthHeaders()
      });
      setAds(response.data.ads || []);
    } catch (err) {
      console.error('Failed to fetch ads:', err);
    } finally {
      setLoading(false);
    }
  };
  
  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/advertising/admin/stats`, {
        headers: getAuthHeaders()
      });
      setStats({
        pending: response.data.pending_review || 0,
        approved: response.data.active_ads || 0,
        rejected: 0 // Would come from backend
      });
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    }
  };
  
  const handleApprove = async (adId) => {
    setProcessing(true);
    try {
      await axios.put(`${API}/advertising/admin/ads/${adId}/approve`, {}, {
        headers: getAuthHeaders()
      });
      toast.success('Ad approved');
      fetchAds();
      fetchStats();
    } catch (err) {
      toast.error('Failed to approve ad');
    } finally {
      setProcessing(false);
    }
  };
  
  const handleReject = async () => {
    if (!selectedAd || !rejectReason.trim()) {
      toast.error('Please provide a rejection reason');
      return;
    }
    
    setProcessing(true);
    try {
      await axios.put(
        `${API}/advertising/admin/ads/${selectedAd.ad_id}/reject?reason=${encodeURIComponent(rejectReason)}`,
        {},
        { headers: getAuthHeaders() }
      );
      toast.success('Ad rejected');
      setShowRejectDialog(false);
      setSelectedAd(null);
      setRejectReason('');
      fetchAds();
      fetchStats();
    } catch (err) {
      toast.error('Failed to reject ad');
    } finally {
      setProcessing(false);
    }
  };
  
  const openRejectDialog = (ad) => {
    setSelectedAd(ad);
    setShowRejectDialog(true);
  };
  
  return (
    <div className="space-y-6" data-testid="admin-ad-approval">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
            Ad Approval
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Review and approve advertiser submissions
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchAds}>
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>
      
      {/* Stats */}
      <div className="grid grid-cols-3 gap-3 sm:gap-4">
        <Card className={activeTab === 'pending_review' ? 'ring-2 ring-yellow-500' : ''}>
          <CardContent className="p-3 sm:p-4">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-yellow-100 dark:bg-yellow-900 rounded-full flex items-center justify-center">
                <Clock className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-600 dark:text-yellow-400" />
              </div>
              <div>
                <p className="text-xl sm:text-2xl font-bold">{stats.pending}</p>
                <p className="text-xs sm:text-sm text-gray-500">Pending</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-3 sm:p-4">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center">
                <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <p className="text-xl sm:text-2xl font-bold text-green-600">{stats.approved}</p>
                <p className="text-xs sm:text-sm text-gray-500">Approved</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-3 sm:p-4">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-red-100 dark:bg-red-900 rounded-full flex items-center justify-center">
                <Ban className="w-4 h-4 sm:w-5 sm:h-5 text-red-600 dark:text-red-400" />
              </div>
              <div>
                <p className="text-xl sm:text-2xl font-bold text-red-600">{stats.rejected}</p>
                <p className="text-xs sm:text-sm text-gray-500">Rejected</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="w-full sm:w-auto">
          <TabsTrigger value="pending_review" className="flex-1 sm:flex-none">
            Pending
            {stats.pending > 0 && (
              <Badge className="ml-2 bg-yellow-500">{stats.pending}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="approved" className="flex-1 sm:flex-none">Approved</TabsTrigger>
          <TabsTrigger value="rejected" className="flex-1 sm:flex-none">Rejected</TabsTrigger>
        </TabsList>
        
        <TabsContent value={activeTab} className="mt-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
            </div>
          ) : ads.length === 0 ? (
            <Card>
              <CardContent className="p-8 text-center">
                <Sparkles className="w-12 h-12 mx-auto text-gray-300 mb-4" />
                <p className="text-gray-500">No {activeTab.replace('_', ' ')} ads</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {ads.map((ad) => (
                <AdReviewCard
                  key={ad.ad_id}
                  ad={ad}
                  onApprove={() => handleApprove(ad.ad_id)}
                  onReject={() => openRejectDialog(ad)}
                  processing={processing}
                  showActions={activeTab === 'pending_review'}
                />
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
      
      {/* Reject Dialog */}
      <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Reject Ad</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
              Please provide a reason for rejecting "{selectedAd?.title}"
            </p>
            <Textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="e.g., Inappropriate content, misleading claims, broken link..."
              rows={3}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRejectDialog(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleReject}
              disabled={processing || !rejectReason.trim()}
            >
              {processing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Reject Ad
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/**
 * AdReviewCard - Individual ad card for review
 */
function AdReviewCard({ ad, onApprove, onReject, processing, showActions }) {
  const [expanded, setExpanded] = useState(false);
  
  return (
    <Card className="overflow-hidden">
      <CardContent className="p-4">
        <div className="flex gap-4">
          {/* Image */}
          {ad.image_url && (
            <div className="w-20 h-20 sm:w-24 sm:h-24 flex-shrink-0 bg-gray-100 dark:bg-gray-800 rounded-lg overflow-hidden">
              <img
                src={ad.image_url}
                alt={ad.title}
                className="w-full h-full object-cover"
                onError={(e) => e.target.style.display = 'none'}
              />
            </div>
          )}
          
          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-white line-clamp-1">
                  {ad.title}
                </h3>
                <p className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1 mt-1">
                  <Building className="w-3 h-3" />
                  {ad.advertiser_id?.slice(0, 8)}...
                </p>
              </div>
              <Badge variant={
                ad.status === 'approved' ? 'default' :
                ad.status === 'rejected' ? 'destructive' :
                'secondary'
              }>
                {ad.status?.replace('_', ' ')}
              </Badge>
            </div>
            
            <p className="text-sm text-gray-600 dark:text-gray-300 mt-2 line-clamp-2">
              {ad.description}
            </p>
            
            {/* Meta */}
            <div className="flex flex-wrap items-center gap-3 mt-3 text-xs text-gray-500">
              <span className="flex items-center gap-1">
                <DollarSign className="w-3 h-3" />
                ${ad.budget_daily}/day
              </span>
              <a
                href={ad.link_url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 text-blue-600 hover:underline"
              >
                <ExternalLink className="w-3 h-3" />
                View Link
              </a>
            </div>
            
            {/* Rejection reason if rejected */}
            {ad.status === 'rejected' && ad.rejection_reason && (
              <div className="mt-3 p-2 bg-red-50 dark:bg-red-900/20 rounded text-sm text-red-600 dark:text-red-400">
                <strong>Reason:</strong> {ad.rejection_reason}
              </div>
            )}
            
            {/* Actions */}
            {showActions && (
              <div className="flex gap-2 mt-4">
                <Button
                  size="sm"
                  className="bg-green-600 hover:bg-green-700"
                  onClick={onApprove}
                  disabled={processing}
                >
                  <Check className="w-4 h-4 mr-1" />
                  Approve
                </Button>
                <Button
                  size="sm"
                  variant="destructive"
                  onClick={onReject}
                  disabled={processing}
                >
                  <X className="w-4 h-4 mr-1" />
                  Reject
                </Button>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
